export type FleetQuestionOption = {
  id: string;
  label: string;
};

export type FleetQuestionItem = {
  id: string;
  prompt: string;
  options: FleetQuestionOption[];
  allowMultiple?: boolean;
};

export type FleetQuestionRequest = {
  id: string;
  sessionKey: string;
  title: string;
  questions: FleetQuestionItem[];
  createdAtMs: number;
  expiresAtMs: number;
  /** Set after the operator submits an answer. The interactive card is
   *  dropped from the active prompt immediately; a compact "You answered"
   *  echo bubble replaces it until a newer assistant turn lands. */
  answered?: FleetQuestionAnswerSummary;
};

export type FleetQuestionAnswerSummary = {
  /** Map of questionId -> chosen option IDs (preserves submit order). */
  selectedOptionIds: Record<string, string[]>;
  /** Map of questionId -> free-text answer entered. */
  freeText: Record<string, string>;
  /** Map of questionId -> chosen option labels (for gray-out echo). */
  selectedOptionLabels: Record<string, string[]>;
  submittedAtMs: number;
};

/** Persisted answered-question record, kept across page refreshes so the
 *  gray-out state survives a hard reload. Stored in localStorage keyed
 *  by sessionKey + question id. */
export type PersistedFleetQuestionAnswer = {
  sessionKey: string;
  request: FleetQuestionRequest;
  savedAtMs: number;
};

/** One answer entry of the compact "You answered" echo bubble rendered
 *  after the operator submits. The view shows selected labels (and
 *  optional free-text) — not the full option list. `options` +
 *  `allowMultiple` remain required so older writers stay safe if a
 *  template still maps options. */
export type FleetQuestionLastAnswerEntry = {
  questionId: string;
  questionPrompt: string;
  allowMultiple: boolean;
  options: FleetQuestionOption[];
  selectedOptionIds: string[];
  selectedOptionLabels: string[];
  freeText: string | null;
};

export type FleetQuestionLastAnswer = {
  requestTitle: string;
  answers: FleetQuestionLastAnswerEntry[];
  submittedAtMs: number;
};

// ---- localStorage persistence ---------------------------------------------

const FLEET_ANSWERS_KEY_PREFIX = "jaisiu.fleetAnswers";

function answersKey(sessionKey: string): string {
  return `${FLEET_ANSWERS_KEY_PREFIX}.${sessionKey}`;
}

/** Save an answered question for the given session. Survives reload. */
export function persistFleetAnswer(sessionKey: string, request: FleetQuestionRequest): void {
  if (typeof localStorage === "undefined" || !request.answered) {
    return;
  }
  try {
    const map = readPersistedAnswers(sessionKey);
    map[request.id] = {
      sessionKey,
      request,
      savedAtMs: Date.now(),
    };
    localStorage.setItem(answersKey(sessionKey), JSON.stringify(map));
  } catch {
    // localStorage may be disabled — fail silently.
  }
}

/** Delete a single answered record (called when the entry drops out of
 *  the queue on a NEW question arriving for the same id). */
export function dropPersistedFleetAnswer(sessionKey: string, id: string): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    const map = readPersistedAnswers(sessionKey);
    if (id in map) {
      delete map[id];
      localStorage.setItem(answersKey(sessionKey), JSON.stringify(map));
    }
  } catch {
    // ignore
  }
}

/** Read every answered entry persisted for a session. Records older than
 *  the gate's question TTL (30 minutes) are pruned. */
export function readPersistedFleetAnswers(
  sessionKey: string,
  ttlMs = 30 * 60 * 1000,
): FleetQuestionRequest[] {
  if (typeof localStorage === "undefined") {
    return [];
  }
  const map = readPersistedAnswers(sessionKey);
  const now = Date.now();
  const live: FleetQuestionRequest[] = [];
  for (const id of Object.keys(map)) {
    const entry = map[id];
    if (now - entry.savedAtMs > ttlMs) {
      delete map[id];
      continue;
    }
    live.push(entry.request);
  }
  try {
    localStorage.setItem(answersKey(sessionKey), JSON.stringify(map));
  } catch {
    // ignore
  }
  return live;
}

function readPersistedAnswers(sessionKey: string): Record<string, PersistedFleetQuestionAnswer> {
  try {
    const raw = localStorage.getItem(answersKey(sessionKey));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return {};
    return parsed as Record<string, PersistedFleetQuestionAnswer>;
  } catch {
    return {};
  }
}

export type FleetQuestionAnswer = {
  questionId: string;
  selectedOptionIds: string[];
  freeText?: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseQuestionItems(raw: unknown): FleetQuestionItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: FleetQuestionItem[] = [];
  for (const item of raw) {
    if (!isRecord(item)) {
      continue;
    }
    const id = typeof item.id === "string" ? item.id.trim() : "";
    const prompt = typeof item.prompt === "string" ? item.prompt.trim() : "";
    if (!id || !prompt) {
      continue;
    }
    const optionsRaw = item.options;
    if (!Array.isArray(optionsRaw)) {
      continue;
    }
    const options: FleetQuestionOption[] = [];
    for (const opt of optionsRaw) {
      if (!isRecord(opt)) {
        continue;
      }
      const optId = typeof opt.id === "string" ? opt.id.trim() : "";
      const label = typeof opt.label === "string" ? opt.label.trim() : "";
      if (optId && label) {
        options.push({ id: optId, label });
      }
    }
    if (options.length === 0) {
      continue;
    }
    out.push({
      id,
      prompt,
      options,
      allowMultiple: item.allowMultiple === true,
    });
  }
  return out;
}

export function parseFleetQuestionRequested(payload: unknown): FleetQuestionRequest | null {
  if (!isRecord(payload)) {
    return null;
  }
  const id = typeof payload.id === "string" ? payload.id.trim() : "";
  const sessionKey = typeof payload.sessionKey === "string" ? payload.sessionKey.trim() : "";
  const title = typeof payload.title === "string" ? payload.title.trim() : "";
  const questions = parseQuestionItems(payload.questions);
  const createdAtMs = typeof payload.createdAtMs === "number" ? payload.createdAtMs : 0;
  const expiresAtMs = typeof payload.expiresAtMs === "number" ? payload.expiresAtMs : 0;
  if (!id || !sessionKey || !title || questions.length === 0 || !createdAtMs || !expiresAtMs) {
    return null;
  }
  return { id, sessionKey, title, questions, createdAtMs, expiresAtMs };
}

export function parseFleetQuestionResolved(payload: unknown): { id: string } | null {
  if (!isRecord(payload)) {
    return null;
  }
  const id = typeof payload.id === "string" ? payload.id.trim() : "";
  if (!id) {
    return null;
  }
  return { id };
}

export function pruneFleetQuestionQueue(queue: FleetQuestionRequest[]): FleetQuestionRequest[] {
  const now = Date.now();
  return queue.filter((entry) => entry.expiresAtMs > now);
}

export function addFleetQuestion(
  queue: FleetQuestionRequest[],
  entry: FleetQuestionRequest,
): FleetQuestionRequest[] {
  // Drop already-answered entries — a new question supersedes the
  // previous answer echo (the operator already saw the gray-out + the
  // "You answered" bubble, and a fresh prompt deserves a fresh row).
  const next = pruneFleetQuestionQueue(queue).filter(
    (item) => item.id !== entry.id && !item.answered,
  );
  next.push(entry);
  return next;
}

export function removeFleetQuestion(
  queue: FleetQuestionRequest[],
  id: string,
): FleetQuestionRequest[] {
  return pruneFleetQuestionQueue(queue).filter((entry) => entry.id !== id);
}

/** Mark a queued question as answered (does NOT remove it). Prefer
 *  `removeFleetQuestion` on submit so the interactive card collapses
 *  into the echo bubble immediately; this helper remains for tests and
 *  persistence paths that need the answered summary on the entry. */
export function markFleetQuestionAnswered(
  queue: FleetQuestionRequest[],
  id: string,
  summary: FleetQuestionAnswerSummary,
): FleetQuestionRequest[] {
  return queue.map((entry) => (entry.id === id ? { ...entry, answered: summary } : entry));
}

/** True when the question entry has been answered by the operator. */
export function isFleetQuestionAnswered(entry: FleetQuestionRequest | undefined | null): boolean {
  return Boolean(entry && entry.answered);
}

export function fleetQuestionsForSession(
  queue: FleetQuestionRequest[],
  sessionKey: string,
): FleetQuestionRequest[] {
  const key = sessionKey.trim();
  if (!key) {
    return [];
  }
  return pruneFleetQuestionQueue(queue).filter((entry) => entry.sessionKey === key);
}

/** Pending (unanswered) questions for a session — interactive card only. */
export function pendingFleetQuestionsForSession(
  queue: FleetQuestionRequest[],
  sessionKey: string,
): FleetQuestionRequest[] {
  return fleetQuestionsForSession(queue, sessionKey).filter((entry) => !entry.answered);
}

/**
 * Compact one-line summary for an echo entry (selected labels + free text).
 */
export function formatFleetAnswerEchoValue(entry: {
  selectedOptionLabels: string[];
  freeText: string | null;
}): string {
  const picks = entry.selectedOptionLabels.filter((label) => label.trim().length > 0);
  const free = (entry.freeText ?? "").trim();
  if (picks.length > 0 && free) {
    return `${picks.join(", ")} · "${free}"`;
  }
  if (picks.length > 0) {
    return picks.join(", ");
  }
  if (free) {
    return free;
  }
  return "(none)";
}

/**
 * Drop the sticky answer echo once a newer assistant turn is in the
 * transcript so the thread can proceed past the Ask answer.
 */
export function shouldClearFleetAnswerEcho(params: {
  answerSubmittedAtMs: number | null | undefined;
  newestAssistantTimestampMs: number | null | undefined;
}): boolean {
  const submitted = params.answerSubmittedAtMs;
  if (submitted == null) {
    return false;
  }
  return !shouldKeepPollingAfterFleetAnswer(params);
}

export function isFleetQuestionDraftComplete(
  request: FleetQuestionRequest,
  draft: Record<string, string[]>,
  freeTextByQuestion: Record<string, string> = {},
): boolean {
  for (const q of request.questions) {
    const selected = draft[q.id] ?? [];
    const freeText = (freeTextByQuestion[q.id] ?? "").trim();
    // Accept EITHER a selected option OR a non-empty free-text answer.
    if (selected.length === 0 && freeText.length === 0) {
      return false;
    }
  }
  return true;
}

export function buildFleetQuestionAnswers(
  request: FleetQuestionRequest,
  draft: Record<string, string[]>,
  freeTextByQuestion: Record<string, string>,
): FleetQuestionAnswer[] {
  return request.questions.map((q) => ({
    questionId: q.id,
    selectedOptionIds: [...(draft[q.id] ?? [])],
    freeText: freeTextByQuestion[q.id]?.trim() || null,
  }));
}

/**
 * After the operator answers Ask, keep history polling until a newer
 * assistant timestamp lands. `fleetQuestionLastAnswer` is set on submit,
 * so treating its presence as "done" stopped the poll before the agent
 * wrote the follow-up.
 */
export function shouldKeepPollingAfterFleetAnswer(params: {
  answerSubmittedAtMs: number | null | undefined;
  newestAssistantTimestampMs: number | null | undefined;
}): boolean {
  const submitted = params.answerSubmittedAtMs;
  if (submitted == null) {
    return false;
  }
  const assistantTs = params.newestAssistantTimestampMs;
  if (assistantTs == null) {
    return true;
  }
  return assistantTs < submitted;
}
